import {useLayoutEffect, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {applicationSelector, updateDesign, updateAppTab} from '../../../slices/application';
import {saveDesignToDynamo, getLatestDesign, getDesignById} from '../../../api';
import Canvas from '../../Canvas/Canvas';
import Footer from '../../Footer/Footer';
import Forms from '../../Forms/Forms';
import Header from '../../Header/Header';
import SaveModal from '../../Modal/SaveModal';
import DisclaimerModal from '../../Modal/DisclaimerModal';
import InfoModal from '../../Modal/InfoModal';
import Nav from '../../Nav/Nav';
import Tooltip from '../../Tooltip/Tooltip';
import {useShadowRoot} from '../../../contexts/ShadowRootContext';
import './Printing.css';

/**
 * Printing - Main printing page component.
 * Renders the full design editor with forms, canvas, and navigation.
 * Gets uuid/designId from Redux state (set by HeatshrinkEditor).
 *
 * @param {Object} props
 * @param {function} props.onNavigateToConfirm - Callback when navigating to confirm view
 * @param {function} props.onEditingChange - Callback when editing state changes.
 *   Signature: (isEditing: boolean) => void
 * @param {function} props.onOrderSubmit - Callback when order is submitted.
 *   Signature: (orderData: object) => void
 */
const Printing = ({onNavigateToConfirm, onEditingChange, onOrderSubmit}) => {
  const {uuid, suppliedDesignId} = useSelector(applicationSelector);
  const shadowRoot = useShadowRoot();
  const {design, appTab} = useSelector(applicationSelector, (oldState, newState) => {
    return (
      oldState.design.objects === newState.design.objects &&
      oldState.design.id === newState.design.id &&
      oldState.design.heatshrinkColor === newState.design.heatshrinkColor &&
      oldState.design.printColor === newState.design.printColor &&
      oldState.design.diameter === newState.design.diameter &&
      oldState.design.finish === newState.design.finish &&
      oldState.design.shrinkRatio === newState.design.shrinkRatio &&
      oldState.design.length === newState.design.length &&
      oldState.design.orderInstructions === newState.design.orderInstructions &&
      oldState.appTab === newState.appTab
    );
  });

  const {currentKonvaObject} = useSelector(applicationSelector, (oldState, newState) => {
    return oldState.currentKonvaObject?.id === newState.currentKonvaObject?.id;
  });

  const previousDiameter = useRef((design.diameter * 3.14) / 2);
  const previousLength = useRef(design.length * 1.05);
  const previousId = useRef(localStorage.getItem('lastDesign'));

  const dispatch = useDispatch();

  const updateDesignLocalstorage = function (designId) {
    localStorage.setItem('lastDesign', designId);
  };

  const updateDesignWithCanvasSize = function (design) {
    let drawing = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');

    if (!drawing) {
      return;
    }
    let counter = 0;
    while (
      (drawing?.clientHeight === 0 || drawing?.clientHeight == null) &&
      appTab === 'select-options' &&
      counter < 100
    ) {
      drawing = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');
      counter++;
    }

    // Width comes from the wrapper (the stable viewport). The drawing layer itself
    // grows wider than the viewport when a slender tube overflows for horizontal
    // scroll, so measuring it here would feed the enlarged width back into the
    // geometry — flipping the tube out of overflow mode and breaking the scroll.
    const wrapper =
      shadowRoot.getElementsByClassName?.('hs-canvas-wrapper')?.[0] ||
      shadowRoot.querySelector?.('.hs-canvas-wrapper');
    const viewportWidth = wrapper?.clientWidth ?? drawing?.clientWidth;

    if (drawing?.clientHeight > 0 && viewportWidth > 0) {
      dispatch(
        updateDesign({
          ...design,
          canvasHeight: drawing.clientHeight,
          canvasWidth: viewportWidth
        })
      );
    } else {
      console.error('Cannot update design with a canvas of zero width or height');
    }
  };

  async function fetchLatestDesign(userUUID) {
    try {
      const response = await getLatestDesign(userUUID);
      updateDesignLocalstorage(response.data.designId);
      updateDesignWithCanvasSize({...response.data});
      previousDiameter.current = (response.data.diameter * 3.14) / 2;
      previousLength.current = response.data.length * 1.05;
      dispatch(updateDesign({...response.data}));
    } catch (error) {
      console.log(error.message);
    }
  }

  async function fetchDesignById(designId) {
    try {
      const response = await getDesignById(designId);
      updateDesignLocalstorage(response.data.designId);
      updateDesignWithCanvasSize({...response.data});
      previousDiameter.current = (response.data.diameter * 3.14) / 2;
      previousLength.current = response.data.length * 1.05;
      dispatch(updateDesign({...response.data}));
    } catch (error) {
      console.log(error.message);
    }
  }

  function repositionObjects() {
    const yFactor = (design.length * 1.05) / previousLength.current;
    const xFactor = (design.diameter * 3.14) / 2 / previousDiameter.current;

    const objects = design.objects.map((obj) => {
      const copy = {...obj};
      try {
        copy.scaleX = obj.scaleX * xFactor;
        copy.scaleY = obj.scaleY * yFactor;
      } catch {
        copy.scaleX = 0;
        copy.scaleY = 0;
      }
      return copy;
    });

    previousDiameter.current = (design.diameter * 3.14) / 2;
    previousLength.current = design.length * 1.05;

    dispatch(updateDesign({...design, objects: objects}));
  }

  const saveDesignAsync = async function (design) {
    if (design.designId == '') {
      dispatch(updateDesign({...design, designId: localStorage.getItem('lastDesign')}));
    }
    try {
      const response = await saveDesignToDynamo(design);
      if (response?.data?.designId != design.designId) {
        updateDesignLocalstorage(response.data.designId);
        const updatedData = {
          ...response.data,
          ...(uuid && {userUUID: uuid, user_uuid: uuid, userId: uuid})
        };

        dispatch(updateDesign(updatedData));
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  };

  useEffect(() => {
    const lastDesign = localStorage.getItem('lastDesign');
    if (lastDesign) {
      fetchDesignById(lastDesign);
    }
  }, []);

  useEffect(() => {
    if (uuid) {
      dispatch(updateDesign({...design, userUUID: uuid, user_uuid: uuid, userId: uuid}));
    }
  }, [uuid]);

  useEffect(() => {
    const isEditing = currentKonvaObject != null;
    onEditingChange?.(isEditing);
  }, [currentKonvaObject, onEditingChange]);

  // Federated version: use Redux state instead of query params
  useLayoutEffect(() => {
    if (suppliedDesignId) {
      fetchDesignById(suppliedDesignId);
      dispatch(updateAppTab('choose-quantity'));
    } else if (uuid) {
      fetchLatestDesign(uuid);
    }
  }, []);

  useEffect(() => {
    const drawing = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');
    if (drawing?.clientHeight > 0 && drawing?.clientWidth > 0) {
      updateDesignWithCanvasSize(design);
    }
  }, [appTab, shadowRoot]);

  useEffect(() => {
    if (design?.designId != previousId.current || appTab != 'choose-tubing') {
      return;
    }
    if (
      design.length * 1.05 != previousLength.current ||
      (design.diameter * 3.14) / 2 != previousDiameter.current
    ) {
      repositionObjects();
    }
  }, [design.length, design.diameter]);

  useEffect(() => {
    if (design.designId === '') {
      const lastDesign = localStorage.getItem('lastDesign');
      if (lastDesign) {
        dispatch(updateDesign({...design, designId: lastDesign}));
      }
    }
    if (design.designId != '') {
      previousId.current = design.designId;
      saveDesignAsync(design);
    }
  }, [
    design.heatshrinkColor,
    design.printColor,
    design.diameter,
    design.finish,
    design.shrinkRatio,
    design.length,
    design.objects,
    design.orderInstructions,
    design.designId
  ]);

  return (
    <main className="hs-page hs-lab printing">
      <div className="container hs-page-container">
        <Header />
        <section className="hs-designer">
          <Nav />
          <Forms onNavigateToConfirm={onNavigateToConfirm} onOrderSubmit={onOrderSubmit} />
          <Canvas />
        </section>
        <Footer />
      </div>
      <Tooltip />
      <SaveModal />
      <DisclaimerModal />
      <InfoModal />
    </main>
  );
};

export default Printing;
